import {
  channelPermissions,
  permissionGroups,
  permissionInfo,
  permissions,
  type ChannelPermission,
  type Permission,
  type PermissionGroup,
} from '@nexo/types';
import { cn } from '@nexo/ui';
import { Check, Minus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const byGroup = (list: readonly Permission[]) =>
  (Object.keys(permissionGroups) as PermissionGroup[])
    .map((group) => ({
      group,
      items: list.filter((p) => permissionInfo[p].group === group),
    }))
    .filter((g) => g.items.length > 0);

/** On/off list of permissions (roles and the base role), grouped like Discord. */
export function PermissionSwitches({
  value,
  onChange,
  grantable,
  disabled,
}: {
  value: Permission[];
  onChange: (next: Permission[]) => void;
  /** What the current user can hand out; the rest is shown but locked. */
  grantable: Permission[];
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const toggle = (p: Permission, on: boolean) =>
    onChange(on ? [...value, p] : value.filter((v) => v !== p));
  return (
    <div className="flex flex-col gap-5">
      {byGroup(permissions).map(({ group, items }) => (
        <section key={group}>
          <h4 className="mb-1 text-[11px] font-bold tracking-wider text-subtle uppercase">
            {t(permissionGroups[group])}
          </h4>
          <ul className="flex flex-col divide-y divide-border">
            {items.map((p) => {
              const checked = value.includes(p);
              const locked = disabled || !grantable.includes(p);
              return (
                <li key={p}>
                  <label
                    className={cn(
                      'flex items-center gap-3 py-2.5',
                      locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                    )}
                    title={
                      locked && !disabled ? t('No puedes dar un permiso que no tienes') : undefined
                    }
                  >
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          'block text-sm',
                          p === 'administrator' && 'font-medium text-danger',
                        )}
                      >
                        {t(permissionInfo[p].label)}
                      </span>
                      <span className="block text-xs text-muted">
                        {t(permissionInfo[p].description)}
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={checked}
                      disabled={locked}
                      onChange={(e) => toggle(p, e.target.checked)}
                      className="peer sr-only"
                    />
                    <span
                      aria-hidden
                      className={cn(
                        'relative h-5 w-9 shrink-0 rounded-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-accent',
                        checked ? 'bg-accent' : 'bg-border-strong',
                      )}
                    >
                      <span
                        className={cn(
                          'absolute top-0.5 size-4 rounded-full bg-white shadow transition-transform',
                          checked ? 'translate-x-4.5' : 'translate-x-0.5',
                        )}
                      />
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

type State = 'deny' | 'inherit' | 'allow';

const states: { state: State; label: string; icon: typeof X; active: string }[] = [
  { state: 'deny', label: 'Denegar', icon: X, active: 'bg-danger text-white' },
  { state: 'inherit', label: 'Heredar', icon: Minus, active: 'bg-surface text-text shadow-panel' },
  { state: 'allow', label: 'Permitir', icon: Check, active: 'bg-success text-white' },
];

/**
 * Channel override editor: each channel permission is denied, inherited from the roles or allowed
 * (Discord's ✕ / ／ / ✓).
 */
export function OverrideEditor({
  allow,
  deny,
  onChange,
  grantable,
}: {
  allow: ChannelPermission[];
  deny: ChannelPermission[];
  onChange: (next: { allow: ChannelPermission[]; deny: ChannelPermission[] }) => void;
  grantable: Permission[];
}) {
  const { t } = useTranslation();
  const set = (p: ChannelPermission, state: State) =>
    onChange({
      allow:
        state === 'allow' ? [...allow.filter((x) => x !== p), p] : allow.filter((x) => x !== p),
      deny: state === 'deny' ? [...deny.filter((x) => x !== p), p] : deny.filter((x) => x !== p),
    });
  return (
    <div className="flex flex-col gap-5">
      {byGroup(channelPermissions).map(({ group, items }) => (
        <section key={group}>
          <h4 className="mb-1 text-[11px] font-bold tracking-wider text-subtle uppercase">
            {t(permissionGroups[group])}
          </h4>
          <ul className="flex flex-col divide-y divide-border">
            {(items as ChannelPermission[]).map((p) => {
              const current: State = allow.includes(p)
                ? 'allow'
                : deny.includes(p)
                  ? 'deny'
                  : 'inherit';
              const locked = !grantable.includes(p);
              return (
                <li key={p} className="flex items-center gap-3 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm">{t(permissionInfo[p].label)}</span>
                    <span className="block text-xs text-muted">
                      {t(permissionInfo[p].description)}
                    </span>
                  </span>
                  <div
                    role="radiogroup"
                    aria-label={t(permissionInfo[p].label)}
                    className={cn(
                      'flex shrink-0 rounded-lg bg-surface-2 p-0.5',
                      locked && 'opacity-60',
                    )}
                  >
                    {states.map(({ state, label, icon: Icon, active }) => (
                      <button
                        key={state}
                        type="button"
                        role="radio"
                        aria-checked={current === state}
                        aria-label={t(label)}
                        title={t(label)}
                        disabled={locked}
                        onClick={() => set(p, state)}
                        className={cn(
                          'flex size-7 items-center justify-center rounded-md text-muted transition disabled:cursor-not-allowed',
                          current === state ? active : 'hover:text-text',
                        )}
                      >
                        <Icon className="size-4" />
                      </button>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
